import autopilotJson from './fixtures/w3/autopilot.json'
import optimizationsJson from './fixtures/w3/optimizations.json'
import rulesJson from './fixtures/w3/rules.json'
import upliftJson from './fixtures/w3/uplift.json'
import campaignsJson from './fixtures/campaigns.json'
import { selectCreativeLibrary } from './creativeSelectors'
import type {
  AccountUplift,
  Ad,
  Audience,
  AutopilotGoal,
  AutopilotState,
  Campaign,
  CampaignStatus,
  DemoDataStateV3,
  Optimization,
  OptimizationDraft,
  OptimizationTargetKind,
  OptimizationType,
  PlanCheckResult,
  ResultMetric,
  Uplift,
} from './types'

interface RawAutopilotItem {
  campaignId: string
  on: boolean
  goal: AutopilotGoal
  target: number
  sinceLabel: string
  lastCheck: string
  nextCheck: string
}

interface RawOptimization {
  id: string
  campaignId: string
  at: string
  type: OptimizationType
  icon: string
  action: string
  why: string
  target: string
  targetKind: OptimizationTargetKind
  undone: boolean
  undoneAt: string | null
}

interface AutopilotRules {
  checkHourLabel: string
  nextCheckLabel: string
  accountCtr: number
  lowCtrFactor: number
  cpaOverFactor: number
  ctrSpreadFactor: number
  resultRateFloor: number
  maxAdjustPerRun: number
  runMs: number
  batchRunMs: number
  itemFadeMs: number
  defaultExpanded: number
  defaultGoalByResultMetric: Record<ResultMetric, AutopilotGoal>
  targetImproveRatio: number
  eligibleStatuses: CampaignStatus[]
  blockedStatus: CampaignStatus
  blockedTip: string
}

interface UpliftFixture {
  items: Uplift[]
  account: AccountUplift
  emptyText: string
  footnote: string
}

const rawAutopilot = autopilotJson as unknown as { items: RawAutopilotItem[] }
const rawOptimizations = optimizationsJson as unknown as { items: RawOptimization[] }
const upliftFixture = upliftJson as unknown as UpliftFixture
const campaignMeta = (campaignsJson as { meta: { today: string; syncedAt: string } }).meta

export const autopilotRules = rulesJson as unknown as AutopilotRules
export const initialUplift = structuredClone(upliftFixture.items)
export const initialAccountUplift = structuredClone(upliftFixture.account)
export const upliftEmptyText = upliftFixture.emptyText
export const upliftFootnote = upliftFixture.footnote
const autopilotDay = campaignMeta.today.slice(5)
const [initialHour, initialMinute] = campaignMeta.syncedAt.split(':').map(Number)
let autopilotMinuteOfDay = initialHour! * 60 + initialMinute!
export let AUTOPILOT_NOW = `${autopilotDay} ${campaignMeta.syncedAt}`

export function advanceAutopilotClock(): string {
  autopilotMinuteOfDay = Math.min(23 * 60 + 59, autopilotMinuteOfDay + 1)
  const hour = String(Math.floor(autopilotMinuteOfDay / 60)).padStart(2, '0')
  const minute = String(autopilotMinuteOfDay % 60).padStart(2, '0')
  AUTOPILOT_NOW = `${autopilotDay} ${hour}:${minute}`
  return AUTOPILOT_NOW
}

const ACCOUNT_CVR = 0.019
const seededStartByCampaign: Record<string, string> = {
  'c-mug-us': '08-28 09:00',
  'c-serum-gb': '08-28 09:00',
}

const productLeadByProduct: Record<string, string> = {
  'p-mug': '轻便随身',
  'p-serum': '28 天临床实证',
  'p-lamp': '护眼光源',
  'p-shoe': '缓震实测',
  'p-pillow': '颈椎支撑',
  'p-fan': '静音实测',
}

const audienceInterestsByProduct: Record<string, string[]> = {
  'p-mug': ['通勤', '户外', '品质生活'],
  'p-serum': ['护肤进阶', '成分党', '抗老修护'],
  'p-lamp': ['阅读', '居家办公', '护眼设备'],
  'p-shoe': ['城市跑步', '运动装备', '健康生活'],
  'p-pillow': ['睡眠改善', '颈椎健康', '家居用品'],
  'p-fan': ['静音家电', '卧室好物', '清凉生活'],
}

export function cloneInitialAutopilot(): Record<string, AutopilotState> {
  return Object.fromEntries(rawAutopilot.items.map((item) => [item.campaignId, {
    on: item.on,
    goal: item.goal,
    target: item.target,
    startedAt: seededStartByCampaign[item.campaignId] ?? item.lastCheck,
    lastCheckAt: item.lastCheck,
    turnedOffAt: null,
  }]))
}

export function cloneInitialOptimizations(): Optimization[] {
  return rawOptimizations.items.map((item) => ({
    ...structuredClone(item),
    payload: null,
    seeded: true,
    reasonKey: `seeded:${item.id}`,
  }))
}

export function deriveAutopilotDefaults(campaign: Campaign): { goal: AutopilotGoal; target: number } {
  const goal = autopilotRules.defaultGoalByResultMetric[campaign.resultMetric]
  const ratio = autopilotRules.targetImproveRatio
  if (goal === 'lower_cpa') {
    const current = campaign.metrics.cpa ?? (campaign.orders > 0 ? campaign.spendMonth / campaign.orders : 0)
    return { goal, target: Math.floor(current * (1 - ratio)) }
  }
  if (goal === 'more_orders') {
    return { goal, target: Math.ceil(campaign.orders * (1 + ratio) / 100) * 100 }
  }
  return { goal, target: Math.ceil(campaign.impressions * (1 + ratio) / 100) * 100 }
}

export function isAutopilotEligible(campaign: Campaign | undefined): { ok: boolean; tip?: string } {
  if (!campaign || campaign.status === autopilotRules.blockedStatus) {
    return { ok: false, tip: autopilotRules.blockedTip }
  }
  return autopilotRules.eligibleStatuses.includes(campaign.status)
    ? { ok: true }
    : { ok: false, tip: '当前状态暂时不能开启托管' }
}

function percent(value: number, digits = 2): string {
  return `${(value * 100).toFixed(digits).replace(/\.00$/, '')}%`
}

function campaignAds(state: DemoDataStateV3, campaignId: string): Ad[] {
  return state.ads.filter((ad) => ad.campaignId === campaignId)
}

function activeRecords(state: DemoDataStateV3, campaignId: string): Optimization[] {
  return state.optimizations.filter((item) => item.campaignId === campaignId && !item.undone)
}

function availableCreative(state: DemoDataStateV3, campaign: Campaign) {
  return selectCreativeLibrary(state.creatives, state.ads, state.campaigns, state.violations, state.risks, {
    productId: campaign.productId,
    usage: 'unused',
  })
    .filter((creative) => creative.compliance === null)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
}

function taskCtr(ads: Ad[]): number {
  const impressions = ads.reduce((sum, ad) => sum + ad.impressions, 0)
  return impressions === 0 ? 0 : ads.reduce((sum, ad) => sum + ad.clicks, 0) / impressions
}

function taskCvr(ads: Ad[]): number {
  const clicks = ads.reduce((sum, ad) => sum + ad.clicks, 0)
  return clicks === 0 ? 0 : ads.reduce((sum, ad) => sum + ad.orders, 0) / clicks
}

function materialDraft(state: DemoDataStateV3, campaign: Campaign, ads: Ad[], records: Optimization[]): OptimizationDraft | null {
  const violation = state.violations.find((item) => {
    const ad = ads.find((candidate) => candidate.id === item.adId)
    return item.campaignId === campaign.id && item.state !== 'resolved' && ad?.status === 'rejected'
  })
  const completed = campaign.status === 'completed'
  if (violation && !completed) {
    const ad = ads.find((item) => item.id === violation.adId)
    if (!ad) return null
    const creative = availableCreative(state, campaign)
    return {
      campaignId: campaign.id,
      type: 'compliance',
      icon: 'clipboard',
      action: '换掉了违规素材，广告已恢复投放',
      why: `1 条广告「${ad.name}」因${violation.reason}被拒登，已停止消耗`,
      target: '投放素材',
      targetKind: 'chip',
      reasonKey: `violation:${violation.id}`,
      effect: { kind: 'creative', adId: ad.id, creativeId: creative?.id ?? null, violationId: violation.id },
    }
  }

  const usedAdIds = new Set(records.flatMap((item) => item.payload?.kind === 'creative' ? [item.payload.adId] : []))
  const lowCtr = ads
    .filter((ad) => ad.impressions > 0 && ad.clicks / ad.impressions < autopilotRules.accountCtr * autopilotRules.lowCtrFactor && !usedAdIds.has(ad.id))
    .sort((a, b) => a.clicks / a.impressions - b.clicks / b.impressions)[0]
  if (!lowCtr) return null
  const ctr = lowCtr.clicks / lowCtr.impressions
  const below = 1 - ctr / autopilotRules.accountCtr
  const creative = availableCreative(state, campaign)
  return {
    campaignId: campaign.id,
    type: 'creative',
    icon: 'sparkle',
    action: creative ? `换上了素材「${creative.name}」` : '生成并换上了一条自动优化素材',
    why: `广告「${lowCtr.name}」点击率 ${percent(ctr)}，比账户基准低 ${Math.round(below * 100)}%`,
    target: '投放素材',
    targetKind: 'chip',
    reasonKey: `low-ctr:${lowCtr.id}`,
    effect: { kind: 'creative', adId: lowCtr.id, creativeId: creative?.id ?? null, violationId: null },
  }
}

function audienceDraft(state: DemoDataStateV3, campaign: Campaign, ads: Ad[]): OptimizationDraft | null {
  if (campaign.audience.type === 'custom') return null
  const autopilot = state.autopilotByCampaign[campaign.id]
  const defaults = deriveAutopilotDefaults(campaign)
  const goal = autopilot?.goal ?? defaults.goal
  const target = autopilot?.target ?? defaults.target
  const ctr = taskCtr(ads)
  const cpa = campaign.orders > 0 ? campaign.spendMonth / campaign.orders : null
  let why: string | null = null
  if (goal === 'lower_cpa' && cpa !== null && cpa > target * autopilotRules.cpaOverFactor) {
    why = `每单成本 $${cpa.toFixed(2)}，比你设的目标 $${target} 高 ${Math.round((cpa / target - 1) * 100)}%`
  } else if (ctr < autopilotRules.accountCtr) {
    why = `这个任务的整体点击率 ${percent(ctr)}，低于账户基准 ${percent(autopilotRules.accountCtr)}`
  } else if (campaign.result.target > 0 && campaign.result.current / campaign.result.target < autopilotRules.resultRateFloor) {
    why = `当前目标进度 ${campaign.result.current.toLocaleString()} / ${campaign.result.target.toLocaleString()}，只完成 ${Math.round(campaign.result.current / campaign.result.target * 100)}%`
  }
  if (!why) return null
  const interests = audienceInterestsByProduct[campaign.productId] ?? ['核心人群', '兴趣拓展', '高意向用户']
  const audience: Audience = { type: 'custom', ageBands: ['25-34', '35-44'], gender: 'all', interests }
  return {
    campaignId: campaign.id,
    type: 'audience',
    icon: 'users',
    action: `把受众从「智能推荐」收窄到 25–44\u00A0岁 · ${interests.slice(0, 2).join(' / ')}`,
    why,
    target: '受众分析',
    targetKind: 'chip',
    reasonKey: 'audience:narrow',
    effect: { kind: 'audience', audience },
  }
}

function strategyDraft(campaign: Campaign, ads: Ad[], records: Optimization[]): OptimizationDraft | null {
  if (records.some((item) => item.type === 'strategy')) return null
  const measured = ads.filter((ad) => ad.impressions > 0)
  if (measured.length < 2) return null
  const sorted = [...measured].sort((a, b) => b.clicks / b.impressions - a.clicks / a.impressions)
  const best = sorted[0]
  const worst = sorted.at(-1)
  if (!best || !worst) return null
  const spread = (best.clicks / best.impressions) / (worst.clicks / worst.impressions)
  if (spread <= autopilotRules.ctrSpreadFactor) return null
  return {
    campaignId: campaign.id,
    type: 'strategy',
    icon: 'target',
    action: '推荐方向换成「高点击场景」',
    why: `表现最好的「${best.name}」点击率是最差一条的 ${spread.toFixed(2)} 倍`,
    target: '创意策略',
    targetKind: 'chip',
    reasonKey: 'strategy:spread',
    effect: { kind: 'variant', key: 'creativeStrategies' },
  }
}

function productDraft(campaign: Campaign, ads: Ad[], records: Optimization[], adjusted: boolean): OptimizationDraft | null {
  if (records.some((item) => item.type === 'product')) return null
  const cvr = taskCvr(ads)
  if (!adjusted && cvr >= ACCOUNT_CVR) return null
  const lead = productLeadByProduct[campaign.productId] ?? '核心卖点'
  return {
    campaignId: campaign.id,
    type: 'product',
    icon: 'bag',
    action: `卖点重排，把「${lead}」提到最前`,
    why: `转化率 ${percent(cvr)}，低于账户平均 ${percent(ACCOUNT_CVR, 1)}`,
    target: '商品分析',
    targetKind: 'chip',
    reasonKey: 'product:conversion',
    effect: { kind: 'variant', key: 'productAnalysis' },
  }
}

function humanDraft(state: DemoDataStateV3, campaign: Campaign, records: Optimization[]): OptimizationDraft | null {
  const existingReasons = new Set(records.filter((item) => item.type === 'needs_human').map((item) => item.reasonKey))
  if (campaign.attentionReason === 'product_link_invalid' && !existingReasons.has('human:broken-link')) {
    return {
      campaignId: campaign.id,
      type: 'needs_human',
      icon: 'alert',
      action: '这个我处理不了，需要你来',
      why: '1 个商品链接失效，广告已停止投放，需要你更新链接',
      target: '更新商品链接',
      targetKind: 'button',
      reasonKey: 'human:broken-link',
      effect: { kind: 'none' },
    }
  }
  const budgetRatio = campaign.budget.total === 0 ? 0 : campaign.spendMonth / campaign.budget.total
  if (budgetRatio > 0.8 && !existingReasons.has('human:budget')) {
    return {
      campaignId: campaign.id,
      type: 'needs_human',
      icon: 'alert',
      action: '这个我处理不了，需要你来',
      why: `本月预算已消耗 ${Math.round(budgetRatio * 100)}%，要不要提额得你定——我不动预算`,
      target: '编辑设置',
      targetKind: 'button',
      reasonKey: 'human:budget',
      effect: { kind: 'none' },
    }
  }
  const completedViolation = state.violations.find((item) => item.campaignId === campaign.id && item.state !== 'resolved')
  const completedKey = completedViolation ? `human:completed-violation:${completedViolation.id}` : ''
  if (campaign.status === 'completed' && completedViolation && !existingReasons.has(completedKey)) {
    return {
      campaignId: campaign.id,
      type: 'needs_human',
      icon: 'alert',
      action: '这个我处理不了，需要你来',
      why: `已完成任务仍有 1 条违规「${completedViolation.reason}」，需要你决定是否申诉`,
      target: '违规处理',
      targetKind: 'button',
      reasonKey: completedKey,
      effect: { kind: 'none' },
    }
  }
  return null
}

export function planCheck(state: DemoDataStateV3, campaignId: string, options: { advanceClock?: boolean } = {}): PlanCheckResult {
  if (options.advanceClock !== false) advanceAutopilotClock()
  const campaign = state.campaigns.find((item) => item.id === campaignId)
  if (!campaign) return { drafts: [], reason: 'no_finding' }
  const ads = campaignAds(state, campaignId)
  const records = activeRecords(state, campaignId)
  const noData = ads.length === 0 || ads.every((ad) => ad.impressions === 0)
  const drafts: OptimizationDraft[] = []
  const material = materialDraft(state, campaign, ads, records)
  if (material) drafts.push(material)

  if (!noData) {
    const audience = audienceDraft(state, campaign, ads)
    if (audience) drafts.push(audience)
    const strategy = strategyDraft(campaign, ads, records)
    if (strategy) drafts.push(strategy)
    const product = productDraft(campaign, ads, records, Boolean(material || audience))
    if (product) drafts.push(product)
  }

  const human = humanDraft(state, campaign, records)
  if (human) drafts.push(human)
  const limited = drafts.slice(0, autopilotRules.maxAdjustPerRun)
  return {
    drafts: limited,
    reason: noData ? 'no_data' : limited.length > 0 ? 'ok' : 'no_finding',
  }
}

export function validateW3Sums(): boolean {
  const autopilotIds = new Set(rawAutopilot.items.map((item) => item.campaignId))
  return rawOptimizations.items.length === 11
    && upliftFixture.items.reduce((sum, item) => sum + item.runs, 0) === upliftFixture.account.weekRuns
    && rawAutopilot.items.length === upliftFixture.account.tracked
    && rawOptimizations.items.every((item) => autopilotIds.has(item.campaignId))
}
